import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from './auth.store';
import { authAxios, refreshAccessToken } from './authAxios';
import SpinnerOverlay from '../shared/components/SpinnerOverlay/SpinnerOverlay';
const mapAuthUser = (raw) => {
    if (!raw)
        return null;
    const firstName = (raw.first_name ?? raw.firstName ?? '');
    const lastName = (raw.last_name ?? raw.lastName ?? '');
    const roleName = (raw.role_name ?? raw.roleName ?? '');
    const roleId = raw.role_id ?? raw.roleId;
    const permissions = (raw.permissions ?? [])
        .map((p) => typeof p === 'string' ? p : p.code)
        .filter(Boolean);
    if (!raw.id || !raw.email || roleId == null)
        return null;
    return {
        id: raw.id,
        email: raw.email,
        firstName,
        lastName,
        fullName: (raw.full_name ?? raw.fullName ?? `${firstName} ${lastName}`.trim()),
        roleId,
        roleName,
        role: roleName,
        permissions,
        isSuperAdmin: raw.is_super_admin ?? raw.isSuperAdmin ?? permissions.includes('super_admin'),
        status: raw.status,
        regionCode: raw.region_code ?? raw.regionCode ?? undefined,
        countryId: raw.country_id ?? raw.countryId ?? undefined,
    };
};
export default function ProtectedRoute({ children }) {
    const { isAuthenticated, isInitializing, user, accessToken, permissionsLoaded, setPermissions, restoreUser } = useAuthStore();
    const navigate = useNavigate();
    const authCheckAttempted = useRef(false);
    const [authChecked, setAuthChecked] = useState(isAuthenticated);
    // Rehydrate from HttpOnly-cookie session before redirecting to login.
    useEffect(() => {
        if (isAuthenticated && accessToken) {
            setAuthChecked(true);
            return;
        }
        if (authCheckAttempted.current)
            return;
        authCheckAttempted.current = true;
        (async () => {
            try {
                const res = await authAxios.get('user-manager/auth/me');
                const payload = res.data?.messages?.[0] ?? res.data?.data ?? res.data;
                const restoredUser = mapAuthUser(payload?.user ?? payload);
                if (!restoredUser)
                    throw new Error('Invalid auth user response.');
                restoreUser(restoredUser);
                if (useAuthStore.getState().refreshToken) {
                    const ok = await refreshAccessToken();
                    if (!ok)
                        throw new Error('Unable to refresh session.');
                }
            }
            catch {
                useAuthStore.getState().clearAuth();
                navigate('/login', { replace: true });
            }
            finally {
                setAuthChecked(true);
            }
        })();
    }, [isAuthenticated, accessToken, navigate, restoreUser]);
    // Load permissions from the roles API exactly once per session.
    useEffect(() => {
        if (!isAuthenticated || !user?.roleId || permissionsLoaded)
            return;
        authAxios
            .get(`user-manager/roles/${user.roleId}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        })
            .then((res) => {
            const roleData = res.data.messages?.[0];
            const codes = roleData?.permissions?.map((p) => p.code) ?? user.permissions;
            setPermissions(codes);
        })
            .catch(() => {
            // Graceful fallback: keep whatever permissions came with the login response.
            setPermissions(user.permissions ?? []);
        });
    }, [isAuthenticated, user?.roleId, permissionsLoaded]);
    if (!authChecked || isInitializing || (isAuthenticated && !permissionsLoaded)) {
        return _jsx(SpinnerOverlay, {});
    }
    if (!isAuthenticated) {
        return _jsx(Navigate, { to: "/login", replace: true });
    }
    return _jsx(_Fragment, { children: children });
}
